/* oxlint-disable typescript/no-explicit-any, typescript/no-unsafe-assignment, typescript/no-unsafe-member-access, typescript/no-unsafe-argument, typescript/no-unsafe-call, typescript/no-unsafe-return, typescript/ban-types -- polymorphic story types; {} is load-bearing for generic defaults */
import type { ObjectSchema, output, SchemaDescriptor } from '@w/schema'
import { use } from 'react'
import type { FunctionComponent, ReactNode } from 'react'
import type { StorybookSettings } from './storybook-settings'
import type { StoryDocs } from './story-docs'

declare const storyBrand: unique symbol
const storyWrapperBrand = Symbol('storyWrapper')
const rootStoryBrand = Symbol('rootStory')
const lazyStoryBrand = Symbol('lazyStory')
const lazyStoriesBrand = Symbol('lazyStories')
const lazyWrapperBrand = Symbol('lazyWrapper')

export type StorySchemaDescriptor = SchemaDescriptor
export type StoryPropsSchema = ObjectSchema<any>

/** Fake-router configuration interpreted by app-owned story shell. */
export type CreateStoryRouterContext<Context = unknown> = (
  execution: PrepareContext,
) => Context | Promise<Context>

export interface StoryRouterParams<Context = unknown> {
  /** App route tree rendered instead of standalone story component. */
  routeTree: any
  /** Creates app-router context once per selected story execution. */
  createCtx: CreateStoryRouterContext<Context>
  /** Resolved runtime context. Storybook supplies this to shells. */
  ctx?: Context
  /** Initial fake browser history. Defaults are shell-owned. */
  initialEntries?: string[]
}

/** Static leaf configuration shared with Storybook renderer and app shell. */
export interface StoryParams<RouterContext = unknown> {
  /** Namespaced app-router configuration. */
  router?: StoryRouterParams<RouterContext>
  /** Place Storybook navigation on right. */
  rightSidebar?: boolean
  /** Story canvas placement. */
  layout?: 'centered' | 'fullscreen'
  /** A story with its own persisted theme provider must not share the host's root theme writer. */
  themeOwnership?: 'host' | 'story'
  /** Opaque documentation payload interpreted by the host's docs renderer. */
  docs?: StoryDocs
}

export type ComponentStoryParams = Omit<StoryParams, 'router'> & {
  router?: never
}

type DeepReadonly<T> = T extends (...args: never[]) => unknown
  ? T
  : T extends object
    ? { readonly [K in keyof T]: DeepReadonly<T[K]> }
    : T

/** Per-render preparation context. Signal aborts on story switch, rerender, or unmount. */
export interface PrepareContext {
  readonly signal: AbortSignal
  readonly settings: DeepReadonly<StorybookSettings>
}

export type Prepare<T = void> = (
  context: PrepareContext,
) => T | Promise<Awaited<T>>

interface StoryConfiguration<
  P,
  T = unknown,
  Params = StoryParams,
  Schema extends StoryPropsSchema = StoryPropsSchema,
> {
  prepare?: Prepare<T>
  params?: Params
  /** Like Storybook's `!dev` tag: absent from sidebar and search, still opens by direct URL (`?id=`). Must be a literal on the story definition; the build-time index (`storybookHiddenStoriesPlugin`) reads it without loading lazy modules. */
  hidden?: boolean
  getProps?: (prepareResult: T) => NoInfer<P>
  schema?: Schema
}

type StoryFn<P = {}> = ((props: P) => ReactNode) & StoryConfiguration<P>

export type Story<P = {}> = StoryFn<P> & {
  readonly [storyBrand]: true
}

export interface LazyStory<S extends Story<any> = Story<any>> {
  readonly [lazyStoryBrand]: true
  /** From the build-time index; the loaded story's own `hidden` is the source. */
  readonly hidden?: boolean
  load: () => Promise<S>
}

type LazyStories<Module extends Record<string, unknown>> = {
  readonly [Key in keyof Module as Module[Key] extends Story<any>
    ? Key
    : never]: Module[Key] extends Story<any> ? LazyStory<Module[Key]> : never
}

export interface StoryWrapper {
  readonly [storyWrapperBrand]: true
  wrapper: FunctionComponent<StoryShellProps<any>>
  prepare?: Prepare
  stories: StoryTreeNode
}

/** Invisible mandatory root scope applied to every story. */
export interface RootStory {
  readonly [rootStoryBrand]: true
  wrapper: FunctionComponent<StoryShellProps<any>>
  prepare?: Prepare
  stories: StoryTreeNode
}

export type StoryTreeNode = {
  [sectionOrPageOrComponentOrStory: string]:
    | StoryTreeNode
    | Story<any>
    | LazyStory
    | StoryWrapper
}

export interface StoryShellProps<RouterContext = unknown> {
  children: ReactNode
  /** Selected leaf's static configuration. */
  params?: StoryParams<RouterContext>
  /** Complete global URL-backed Storybook environment. */
  settings: StorybookSettings
}

interface ComponentConfiguration {
  params?: ComponentStoryParams
  /** See `StoryConfiguration.hidden`. */
  hidden?: boolean
}

type NoSourceStoryOptions<Props> = ComponentConfiguration & {
  prepare?: never
  schema?: never
  getProps?: () => NoInfer<Props>
}

type PreparedStoryOptions<Props, PrepareResult> = ComponentConfiguration & {
  prepare: Prepare<PrepareResult>
  schema?: never
  getProps?: (preparedData: Awaited<PrepareResult>) => NoInfer<Props>
}

type SchemaStoryOptions<
  Props,
  Schema extends StoryPropsSchema,
> = ComponentConfiguration & {
  prepare?: never
  schema: Schema
  getProps?: (controlProps: output<Schema>) => NoInfer<Props>
}

type PreparedSchemaStoryOptions<
  Props,
  PrepareResult extends Record<string, unknown>,
  Schema extends StoryPropsSchema,
> = ComponentConfiguration & {
  prepare: Prepare<PrepareResult>
  schema: Schema
  getProps?: (
    sourceProps: output<Schema> & Awaited<PrepareResult>,
  ) => NoInfer<Props>
}

export type StoryOptions<
  Props = {},
  PrepareResult = unknown,
  Schema extends StoryPropsSchema = StoryPropsSchema,
> =
  | NoSourceStoryOptions<Props>
  | PreparedStoryOptions<Props, PrepareResult>
  | SchemaStoryOptions<Props, Schema>
  | PreparedSchemaStoryOptions<
      Props,
      Extract<PrepareResult, Record<string, unknown>>,
      Schema
    >

export type RoutedPrepareContext<Context> = PrepareContext & {
  readonly ctx: Context
}

export type RoutedStoryOptions<
  CreateContext extends CreateStoryRouterContext,
  PrepareResult = unknown,
> = {
  /** See `StoryConfiguration.hidden`. */
  hidden?: boolean
  prepare?: (
    context: RoutedPrepareContext<Awaited<ReturnType<CreateContext>>>,
  ) => PrepareResult | Promise<Awaited<PrepareResult>>
  params: Omit<StoryParams<Awaited<ReturnType<CreateContext>>>, 'router'> & {
    router: Omit<
      StoryRouterParams<Awaited<ReturnType<CreateContext>>>,
      'createCtx' | 'ctx'
    > & {
      createCtx: CreateContext
      ctx?: never
    }
  }
}

export interface StoryWrapperOptions {
  /** Ordinary React wrapper composed around selected descendant leaf. */
  wrapper: FunctionComponent<StoryShellProps<any>>
  /** Setup run before descendant leaf prepare. Mounted effects own cleanup. */
  prepare?: Prepare
  /** Portable story tree owned by this assembly scope. */
  stories: StoryTreeNode
}

export type RootStoryOptions = StoryWrapperOptions

function mergeStoryConfigurations<
  Props,
  BasePrepareResult,
  OverridePrepareResult,
>(
  baseConfig?: StoryOptions<Props, BasePrepareResult>,
  overrideConfig?: StoryOptions<Props, OverridePrepareResult>,
): StoryOptions<Props, BasePrepareResult | OverridePrepareResult> {
  if (!(baseConfig || overrideConfig)) {
    return {}
  }

  if (!baseConfig) {
    return {
      ...(overrideConfig as StoryOptions<
        Props,
        BasePrepareResult | OverridePrepareResult
      >),
    }
  }

  if (!overrideConfig) {
    return {
      ...(baseConfig as StoryOptions<
        Props,
        BasePrepareResult | OverridePrepareResult
      >),
    }
  }

  const mergedConfig = {
    ...baseConfig,
    ...overrideConfig,
  } as StoryOptions<Props, BasePrepareResult | OverridePrepareResult>

  if (baseConfig.params || overrideConfig.params) {
    mergedConfig.params = {
      ...baseConfig.params,
      ...overrideConfig.params,
    }
  }

  return mergedConfig
}
export function story<
  C extends FunctionComponent<any>,
  Props = Parameters<C>[0] extends undefined ? {} : Parameters<C>[0],
  PrepareResult extends Record<string, unknown> = Record<string, unknown>,
  Schema extends StoryPropsSchema = StoryPropsSchema,
>(
  Component: C,
  configuration: PreparedSchemaStoryOptions<Props, PrepareResult, Schema>,
): Story<Props>
export function story<
  C extends FunctionComponent<any>,
  Props = Parameters<C>[0] extends undefined ? {} : Parameters<C>[0],
  Schema extends StoryPropsSchema = StoryPropsSchema,
>(Component: C, configuration: SchemaStoryOptions<Props, Schema>): Story<Props>
export function story<
  C extends FunctionComponent<any>,
  Props = Parameters<C>[0] extends undefined ? {} : Parameters<C>[0],
  PrepareResult = unknown,
>(
  Component: C,
  configuration: PreparedStoryOptions<Props, PrepareResult>,
): Story<Props>
export function story<
  C extends FunctionComponent<any>,
  Props = Parameters<C>[0] extends undefined ? {} : Parameters<C>[0],
>(Component: C, configuration?: NoSourceStoryOptions<Props>): Story<Props>
export function story<
  C extends FunctionComponent<any>,
  Props = Parameters<C>[0] extends undefined ? {} : Parameters<C>[0],
  PrepareResult = unknown,
  Schema extends StoryPropsSchema = StoryPropsSchema,
>(
  Component: C,
  configuration: StoryOptions<Props, PrepareResult, Schema>,
): Story<Props>
export function story<
  CreateContext extends CreateStoryRouterContext,
  PrepareResult = unknown,
>(configuration: RoutedStoryOptions<CreateContext, PrepareResult>): Story
export function story<
  C extends FunctionComponent<any>,
  Props = Parameters<C>[0] extends undefined ? {} : Parameters<C>[0],
  PrepareResult = unknown,
  Schema extends StoryPropsSchema = StoryPropsSchema,
>(
  ComponentOrConfig: C,
  configuration?: StoryOptions<Props, PrepareResult, Schema>,
): Story<Props> {
  if (typeof ComponentOrConfig !== 'function') {
    const component = () => null
    if (ComponentOrConfig) {
      Object.assign(component, ComponentOrConfig)
    }
    return component as unknown as Story<Props>
  }

  const ForwardedComponent = (props: Props) => {
    // @ts-expect-error -- props type narrowing not possible here due to polymorphic story overload
    if (!props) return <ComponentOrConfig />
    // @ts-expect-error -- props type narrowing not possible here due to polymorphic story overload
    return <ComponentOrConfig {...props} />
  }

  // ForwardedComponent.displayName = Component.displayName || Component.name //

  if (configuration) {
    Object.assign(ForwardedComponent, configuration)
  }

  return ForwardedComponent as unknown as Story<Props>
}

export function storyWrapper(options: StoryWrapperOptions): StoryWrapper {
  return { ...options, [storyWrapperBrand]: true }
}

/** Creates ordered, typed lazy story references backed by one module load. */
export function lazyStories<Module extends Record<string, unknown>>(
  load: () => Promise<Module>,
  /** Injected at build time by `storybookHiddenStoriesPlugin`; never written by hand. */
  index: { hidden?: readonly string[] } = {},
): LazyStories<Module> {
  const hidden = new Set(index.hidden)
  let modulePromise: Promise<Module> | undefined
  const references = new Map<PropertyKey, LazyStory>()

  return new Proxy(
    { [lazyStoriesBrand]: true } as unknown as LazyStories<Module>,
    {
      get(_target, exportName) {
        const existing = references.get(exportName)
        if (existing) return existing

        let storyPromise: Promise<Story> | undefined
        const reference: LazyStory = {
          [lazyStoryBrand]: true,
          hidden: hidden.has(String(exportName)),
          load: () => {
            storyPromise ??= (modulePromise ??= load()).then((loaded) => {
              const candidate = loaded[exportName as keyof Module]
              if (typeof candidate !== 'function') {
                throw new TypeError(
                  `Lazy story export ${String(exportName)} is not a story`,
                )
              }
              return candidate as unknown as Story
            })
            return storyPromise
          },
        }
        references.set(exportName, reference)
        return reference
      },
    },
  )
}

/** Lazily loads a story shell without changing storyWrapper composition. */
export function lazyWrapper<
  Wrapper extends FunctionComponent<StoryShellProps<any>>,
>(load: () => Promise<Wrapper>): Wrapper {
  let componentPromise: Promise<{ default: Wrapper }> | undefined
  let loadedComponent: FunctionComponent<StoryShellProps<any>> | undefined
  const preload = () => {
    componentPromise ??= load().then((defaultExport) => {
      loadedComponent = defaultExport
      return { default: defaultExport }
    })
    return componentPromise
  }
  const LazyWrapperComponent = (props: StoryShellProps<any>) => {
    const LoadedComponent = loadedComponent ?? use(preload()).default
    return <LoadedComponent {...props} />
  }
  return Object.assign(LazyWrapperComponent, {
    [lazyWrapperBrand]: true,
    preload,
  }) as unknown as Wrapper
}

/** Creates global invisible story scope. Passed directly to `<Storybook>`. */
export function rootStory(options: RootStoryOptions): RootStory {
  return { ...options, [rootStoryBrand]: true }
}

export function isStoryWrapper(value: unknown): value is StoryWrapper {
  return (
    typeof value === 'object' && value !== null && storyWrapperBrand in value
  )
}

export function isLazyStory(value: unknown): value is LazyStory {
  return typeof value === 'object' && value !== null && lazyStoryBrand in value
}

interface LazyWrapperReference {
  readonly [lazyWrapperBrand]: true
  preload: () => Promise<{ default: FunctionComponent<StoryShellProps<any>> }>
}

export function isLazyWrapper(value: unknown): value is LazyWrapperReference {
  return (
    value !== null &&
    (typeof value === 'object' || typeof value === 'function') &&
    lazyWrapperBrand in value
  )
}

export function preloadLazyWrapper(value: unknown): Promise<unknown> {
  return isLazyWrapper(value) ? value.preload() : Promise.resolve()
}

export function isLazyStories(value: unknown): boolean {
  return (
    typeof value === 'object' && value !== null && lazyStoriesBrand in value
  )
}
export function withStoryOptions<Props = {}, PrepareResult = unknown>(
  baseConfig: StoryOptions<Props, PrepareResult>,
): StoryOptions<Props, PrepareResult>
export function withStoryOptions<
  Props = {},
  BasePrepareResult = unknown,
  OverridePrepareResult = BasePrepareResult,
>(
  baseConfig: StoryOptions<Props, BasePrepareResult>,
  overrideConfig: StoryOptions<Props, OverridePrepareResult>,
): StoryOptions<Props, BasePrepareResult | OverridePrepareResult>
export function withStoryOptions<
  Props = {},
  BasePrepareResult = unknown,
  OverridePrepareResult = BasePrepareResult,
>(
  baseConfig: StoryOptions<Props, BasePrepareResult>,
  overrideConfig?: StoryOptions<Props, OverridePrepareResult>,
): StoryOptions<Props, BasePrepareResult | OverridePrepareResult> {
  return {
    ...mergeStoryConfigurations<
      Props,
      BasePrepareResult,
      OverridePrepareResult
    >(baseConfig, overrideConfig),
  }
}
